import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Instagram, Search, X, Check, Loader2, 
  AlertCircle, PlayCircle, Image as ImageIcon,
  Heart, MessageCircle, RefreshCw, Link as LinkIcon
} from 'lucide-react';
import Button from '../common/design-system/Button';
import { useAuth } from '../auth/AuthContext';
import api from '../../services/api';
import { classifyInstagramConnectionError } from '../../utils/instagramConnectionState';
import './InstagramContentPicker.scss';

const MediaCard = ({ item, onSelect, onKeyDown, t, inline = false }) => {
  const [imgError, setImgError] = useState(false);
  const mediaSrc = !imgError ? (item.thumbnail_url || item.media_url) : null;

  return (
    <motion.div
      key={item.id}
      className={`media-card${item.already_submitted ? ' already-submitted' : ''}`}
      whileHover={{ y: -2, scale: inline ? 1.01 : 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(item)}
      onKeyDown={(event) => onKeyDown(event, item)}
      role="button"
      tabIndex={item.already_submitted ? -1 : 0}
      aria-disabled={item.already_submitted}
      title={item.already_submitted ? t('picker.already_delivered') : undefined}
    >
      <div className="media-thumbnail">
        {mediaSrc ? (
          <img
            src={mediaSrc}
            alt={item.caption || ''}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="media-fallback">
            <Instagram size={28} />
          </div>
        )}
        {item.media_type === 'VIDEO' && (
          <div className="media-type-icon"><PlayCircle size={18} /></div>
        )}
        {item.media_type === 'CAROUSEL_ALBUM' && (
          <div className="media-type-icon"><ImageIcon size={18} /></div>
        )}
        {item.already_submitted && (
          <div className="media-type-icon submitted">
            <Check size={16} />
          </div>
        )}
      </div>
      <div className="media-info">
        <p className="caption" title={item.caption || undefined}>
          {item.caption ? (item.caption.length > 55 ? `${item.caption.slice(0, 55)}...` : item.caption) : t('picker.no_caption')}
        </p>
        <div className="stats-row">
          <div className="stats-left">
            {item.like_count != null && <span><Heart size={10} /> {item.like_count}</span>}
            {item.comments_count != null && <span><MessageCircle size={10} /> {item.comments_count}</span>}
          </div>
          {item.timestamp && <span className="date">{new Date(item.timestamp).toLocaleDateString()}</span>}
        </div>
      </div>
      {!item.already_submitted && (
        <div className="select-overlay"><Check size={22} /></div>
      )}
    </motion.div>
  );
};

/**
 * InstagramContentPicker Component
 * @param isOpen: bool
 * @param inline: bool
 * @param onClose: fn
 * @param onSelect: fn(item)
 * @param postType: 'reel' | 'story' | 'post'
 * @param offerId: string (optional) — if provided, marks already-submitted posts
 * @param onConnect: fn — called when user needs to connect Instagram (error state)
 */
const InstagramContentPicker = ({ 
  isOpen, 
  inline = false, 
  onClose, 
  onSelect, 
  onConnect,
  onManualFallback,
  postType, 
  offerId 
}) => {
   const { t } = useTranslation('creator');
   const { invalidateInstagramConnection, refreshUser } = useAuth();
   const [media, setMedia] = useState([]);
   const [loading, setLoading] = useState(true);
   const [refreshing, setRefreshing] = useState(false);
   const [error, setError] = useState(null);
   const [searchTerm, setSearchTerm] = useState('');

  const getFriendlyError = useCallback((err) => {
    const { requiresReconnect } = classifyInstagramConnectionError(err);

    if (requiresReconnect) {
      return {
        message: t('picker.connection_help'),
        needsConnection: true,
        invalidatesConnection: true,
      };
    }

    return {
      message: t('picker.error_load'),
      needsConnection: false,
    };
  }, [t]);

  const fetchMedia = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const params = new URLSearchParams();
      if (offerId) params.append('collab_id', offerId);
      if (postType === 'story') params.append('type', 'story');
      if (forceRefresh) params.append('force_refresh', 'true');
      params.append('limit', '50');

      const response = await api.get(`/collabs/collab/my_instagram_media/?${params.toString()}`, {
        skipAuthRedirect: true,
      });

      const rawItems = Array.isArray(response.data) ? response.data : [];
      let filteredMedia = rawItems;
      if (postType === 'reel') {
        filteredMedia = rawItems.filter(m => 
          m.media_product_type === 'REELS' || (m.media_type === 'VIDEO' && m.media_product_type !== 'FEED')
        );
      } else if (postType === 'post') {
        filteredMedia = rawItems.filter(m => m.media_product_type !== 'REELS');
      }

      setMedia(filteredMedia);
    } catch (err) {
      console.error('Error fetching IG media:', err);
      const friendlyError = getFriendlyError(err);
      setError(friendlyError);
      if (friendlyError.invalidatesConnection) {
        invalidateInstagramConnection?.();
        refreshUser?.();
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [postType, offerId, getFriendlyError, invalidateInstagramConnection, refreshUser]);

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [isOpen, fetchMedia]);

  useEffect(() => {
    if (!isOpen || inline) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, inline, onClose]);

  const filteredMedia = media.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    const captionMatch = item.caption?.toLowerCase().includes(term);
    const dateMatch = item.timestamp && new Date(item.timestamp).toLocaleDateString().includes(term);
    return Boolean(captionMatch || dateMatch);
  });

  const selectMedia = (item) => {
    if (item.already_submitted) return;
    onSelect(item);
  };

  const handleMediaKeyDown = (event, item) => {
    if (item.already_submitted) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectMedia(item);
    }
  };

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="picker-state loading">
          <Loader2 className="animate-spin" size={30} />
          <p>{t('picker.loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="picker-state error">
          <AlertCircle size={36} />
          <h4>{t('picker.error_load')}</h4>
          <p>{error.message}</p>
          <div className="error-actions">
            <Button variant="outline" size="sm" onClick={() => fetchMedia(true)}>
              <RefreshCw size={14} className="mr-2" /> {t('picker.retry')}
            </Button>
            {error.needsConnection && onConnect && (
              <Button variant="primary" size="sm" onClick={onConnect}>
                <LinkIcon size={14} className="mr-2" /> {t('picker.connect')}
              </Button>
            )}
            {onManualFallback && (
              <Button variant="outline" size="sm" onClick={onManualFallback}>
                {t('picker.manual_fallback')}
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (media.length === 0) {
      return (
        <div className="picker-state empty">
          <Instagram size={36} opacity={0.3} />
          <p>{t('picker.empty')}</p>
          {postType === 'story' && (
            <p className="sub-note">{t('picker.story_notice')}</p>
          )}
        </div>
      );
    }

    if (filteredMedia.length === 0) {
      return (
        <div className="picker-state empty">
          <Search size={32} opacity={0.3} />
          <p>{t('picker.no_results', { term: searchTerm })}</p>
        </div>
      );
    }

    return (
      <div className="media-grid">
        {filteredMedia.map((item) => (
          <MediaCard
            key={item.id}
            item={item}
            onSelect={selectMedia}
            onKeyDown={handleMediaKeyDown}
            t={t}
            inline={inline}
          />
        ))}
      </div>
    );
  };

  if (inline) {
    return (
      <div className="ig-picker-inline">
        <div className="inline-picker-topbar">
          <div className="inline-picker-title">
            <Instagram size={16} />
            <span>{t('picker.select_title', { type: t(`picker.types.${postType}`) })}</span>
          </div>
          <div className="topbar-actions">
            <button
              type="button"
              className="picker-refresh-btn"
              onClick={() => fetchMedia(true)}
              disabled={loading || refreshing}
              title={t('picker.refresh')}
              aria-label={t('picker.refresh')}
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              <span>{refreshing ? t('picker.refreshing') : t('picker.refresh')}</span>
            </button>
            <button type="button" className="inline-picker-close" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>

        {postType === 'story' && !error && !loading && (
          <div className="picker-story-banner" role="status">
            <AlertCircle size={15} />
            <span>{t('picker.story_notice')}</span>
          </div>
        )}

        <div className="picker-search">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder={t('picker.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="picker-content">
          {renderContent()}
        </div>

        {!error && (
          <div className="picker-footer">
            <p>{t('picker.footer_note')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ig-picker-overlay" onClick={onClose}>
      <motion.div
        className="ig-picker-modal"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="instagram-picker-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="picker-header">
          <div className="header-title">
            <Instagram size={20} className="text-pink-500" />
            <h3 id="instagram-picker-title">{t('picker.select_title', { type: t(`picker.types.${postType}`) })}</h3>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="picker-refresh-btn"
              onClick={() => fetchMedia(true)}
              disabled={loading || refreshing}
              title={t('picker.refresh')}
              aria-label={t('picker.refresh')}
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              <span>{refreshing ? t('picker.refreshing') : t('picker.refresh')}</span>
            </button>
            <button
              type="button"
              className="close-btn"
              onClick={onClose}
              aria-label={t('common.close', 'Close')}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {postType === 'story' && !error && !loading && (
          <div className="picker-story-banner" role="status">
            <AlertCircle size={15} />
            <span>{t('picker.story_notice')}</span>
          </div>
        )}

        <div className="picker-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder={t('picker.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="picker-content">
          {renderContent()}
        </div>

        {!error && (
          <div className="picker-footer">
            <p>{t('picker.footer_note')}</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default InstagramContentPicker;

